import { useRef, useState } from "react";
import { ImagePlus, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

export function MediaUploadField({ label, value, onChange, testId, context = "cms", accept = "image/*", help = "" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", context);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/media/upload", fd);
      onChange(data.url);
      toast.success("Media berhasil diunggah ke storage");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <input className={inp} data-testid={testId} value={value || ""}
          onChange={(e) => onChange(e.target.value)} placeholder="URL media otomatis setelah upload" />
        <label data-testid={`${testId}-upload-button`} className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#141414] px-4 py-2 text-sm font-semibold text-[#141414] hover:bg-[#F4EFE6]">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
          {busy ? "Upload..." : "Upload"}
          <input ref={fileRef} type="file" accept={accept} disabled={busy}
            data-testid={`${testId}-file`} className="sr-only"
            onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      {help && <p className="mt-1 text-xs text-[#8A867C]">{help}</p>}
      {value && (
        <div className="mt-3 overflow-hidden rounded-xl border border-[#E5DEC9] bg-[#F4EFE6] p-2" data-testid={`${testId}-preview`}>
          <img src={value} alt="Preview media" className="max-h-40 w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

export function MediaGalleryUploadField({ label, value, onChange, testId, context = "product" }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const uploadMany = async (files) => {
    const selected = Array.from(files || []);
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of selected) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("context", context);
        const { data } = await api.post("/admin/media/upload", fd);
        uploaded.push(data.url);
      }
      const current = String(value || "").trim();
      onChange([current, ...uploaded].filter(Boolean).join("\n"));
      toast.success(`${uploaded.length} media galeri berhasil diunggah`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <textarea rows={3} className={inp} data-testid={testId} value={value || ""}
        onChange={(e) => onChange(e.target.value)} placeholder="Satu URL per baris" />
      <label data-testid={`${testId}-upload-button`} className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#141414] px-4 py-2 text-sm font-semibold text-[#141414] hover:bg-[#F4EFE6]">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
        {busy ? "Mengunggah..." : "Upload Galeri"}
        <input ref={fileRef} type="file" accept="image/*" multiple disabled={busy}
          data-testid={`${testId}-files`} className="sr-only"
          onChange={(e) => uploadMany(e.target.files)} />
      </label>
    </div>
  );
}